import React from "react";
import { useDialog } from "../context/DialogContext";
import { getLinearLoader, LinearLoaderError } from "../shared/loaders/LinearLoader";
import { DialogDescriptionWithCustomIcon } from "../shared/dialog/Dialog";
import { AnimatedCheckmark } from "../shared/icons/AnimatedCheckmark";
import {
  ACKNOWLEDGE_ACTION,
  ALL_DATA_LOADED_TITLE,
  LOADER_DISMISS_NOTICE,
  WORKBOOK_EDITED_WHILE_LOADING,
} from "../constants";

/**
 * Module-scoped rather than per-hook: one dialog is shared by every loader, so a load starting must
 * cancel an auto-close another loader scheduled. Refreshing several datasources in sequence would
 * otherwise let the first one's timer close a later one's dialog mid-run.
 */
let closeDialogTimeout: NodeJS.Timeout | null = null;

/**
 * The progress dialog a datasource load drives: a linear bar whose percentage comes from how many
 * sections the load announced, an error state that stops it, and a success state that closes
 * itself. Each loader owns its own section list and calls `setSectionCount` before its first
 * `updateDialog`; `reset` belongs in the loader's `finally`.
 */
export function useLoaderDialog() {
  const { showDialog, hideDialog } = useDialog();
  const sectionCountRef = React.useRef<number>(0);

  // No unmount cleanup: the timer is shared, so cancelling it here would strand a dialog another
  // loader is still counting down. Firing after unmount is harmless — hideDialog belongs to the
  // provider, which outlives every loader.
  const clearTimeoutRef = () => {
    if (closeDialogTimeout) {
      clearTimeout(closeDialogTimeout);
      closeDialogTimeout = null;
    }
  };

  const setSectionCount = (count: number) => {
    sectionCountRef.current = count;
  };

  const updateDialog = async (title: string, section: number, loaderError?: LinearLoaderError) => {
    // A load is in progress, so any auto-close still pending from a previous one is stale.
    clearTimeoutRef();
    if (loaderError) {
      if (
        loaderError.error instanceof OfficeExtension.Error &&
        loaderError.error.code === "InvalidOperationInCellEditMode"
      ) {
        loaderError.errorMessage = WORKBOOK_EDITED_WHILE_LOADING;
      }
    }
    const progress = section === 0 ? 0 : Math.min(Math.max((section / sectionCountRef.current) * 100, 0), 100);
    const description = await getLinearLoader(progress, loaderError);
    showDialog({
      title,
      description,
      submit: loaderError
        ? {
            label: ACKNOWLEDGE_ACTION,
            action: hideDialog,
          }
        : undefined,
      preventClose: loaderError ? false : true,
    });
  };

  const showSuccessAndAutoClose = () => {
    showDialog({
      title: ALL_DATA_LOADED_TITLE,
      description: DialogDescriptionWithCustomIcon(LOADER_DISMISS_NOTICE, AnimatedCheckmark),
    });

    closeDialogTimeout = setTimeout(() => {
      hideDialog();
    }, 5000);
  };

  const reset = () => {
    sectionCountRef.current = 0;
  };

  return { setSectionCount, updateDialog, showSuccessAndAutoClose, clearTimeoutRef, reset };
}
